import React from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import {
  UserGroupIcon,
  AcademicCapIcon,
  CurrencyDollarIcon,
  ExclamationTriangleIcon,
  ClockIcon,
  CheckCircleIcon,
  ArrowTrendingUpIcon,
  CakeIcon
} from '@heroicons/react/24/outline';
import { dashboardAPI, activityAPI } from '../services/api';
import { authService } from '../services/auth';
import FestivalWidget from './FestivalWidget';
import LiveClock from './common/LiveClock';


const Dashboard = () => {
  // Check if user is authenticated before making API calls
  const isAuthenticated = authService.isAuthenticated();
  
  console.log('🔍 Dashboard - User authenticated:', isAuthenticated);
  console.log('🔍 Dashboard - Token exists:', !!authService.getToken());
  
  // Single optimized dashboard stats query
  const { data: dashboardStats, isLoading: statsLoading } = useQuery({
    queryKey: ['dashboard-stats'],
    queryFn: () => dashboardAPI.getStats().then(res => res.data),
    enabled: isAuthenticated, // Only run query if authenticated
  });



  // Get current user role for conditional rendering
  const user = authService.getUser();

  const { data: activities, isLoading: activitiesLoading } = useQuery({
    queryKey: ['dashboard-activities'],
    queryFn: () => activityAPI.getDashboard(5).then(res => res.data),
    enabled: isAuthenticated, // Only run query if authenticated
  });

  // Get today's birthdays
  const { data: birthdayStudents } = useQuery({
    queryKey: ['dashboard-birthdays'],
    queryFn: () => dashboardAPI.getBirthdaysToday().then(res => res.data),
    enabled: isAuthenticated, // Only run query if authenticated
  });

  // Dashboard stats are now calculated on the backend

  const stats = [
    {
      name: 'Total Students',
      value: dashboardStats?.total_students || 0,
      icon: UserGroupIcon,
      color: 'from-blue-500 to-blue-600',
      bgColor: 'bg-gradient-to-br from-blue-500/20 to-blue-600/20',
      borderColor: 'border-blue-500/30',
      href: '/students'
    },
    {
      name: 'Active Batches',
      value: dashboardStats?.total_batches || 0,
      icon: AcademicCapIcon,
      color: 'from-green-500 to-green-600',
      bgColor: 'bg-gradient-to-br from-green-500/20 to-green-600/20',
      borderColor: 'border-green-500/30',
      href: '/batches'
    },
    {
      name: 'Enrollments',
      value: dashboardStats?.total_enrollments || 0,
      icon: CheckCircleIcon,
      color: 'from-purple-500 to-purple-600',
      bgColor: 'bg-gradient-to-br from-purple-500/20 to-purple-600/20',
      borderColor: 'border-purple-500/30',
      href: '/enrollments'
    },
    {
      name: 'Pending Fees',
      value: dashboardStats?.pending_fees_count || 0,
      icon: ExclamationTriangleIcon,
      color: 'from-red-500 to-red-600',
      bgColor: 'bg-gradient-to-br from-red-500/20 to-red-600/20',
      borderColor: 'border-red-500/30',
      href: '/fees'
    }
  ];

  const getActivityColor = (activityType) => {
    switch (activityType) {
      case 'student_created':
      case 'student_updated':
        return 'text-blue-400';
      case 'batch_created':
      case 'batch_updated':
        return 'text-green-400';
      case 'enrollment_created':
        return 'text-purple-400';
      case 'enrollment_fee_paid':
      case 'payment_received':
        return 'text-yellow-400';
      default:
        return 'text-gray-400';
    }
  };

  const getActivityIcon = (activityType) => {
    switch (activityType) {
      case 'student_created':
      case 'student_updated':
        return UserGroupIcon;
      case 'batch_created':
      case 'batch_updated':
        return AcademicCapIcon;
      case 'enrollment_created':
        return CheckCircleIcon;
      case 'enrollment_fee_paid':
      case 'payment_received':
        return CurrencyDollarIcon;
      default:
        return ClockIcon;
    }
  };

  const quickActions = [
    {
      name: 'Add New Student',
      href: '/students/new',
      icon: UserGroupIcon,
      color: 'from-blue-500 to-blue-600',
      bgColor: 'bg-gradient-to-r from-blue-500/10 to-blue-600/10',
      hoverColor: 'hover:from-blue-500/20 hover:to-blue-600/20'
    },
    {
      name: 'Create New Batch',
      href: '/batches/new',
      icon: AcademicCapIcon,
      color: 'from-green-500 to-green-600',
      bgColor: 'bg-gradient-to-r from-green-500/10 to-green-600/10',
      hoverColor: 'hover:from-green-500/20 hover:to-green-600/20'
    },
    {
      name: 'Enroll Student',
      href: '/enrollments/new',
      icon: CheckCircleIcon,
      color: 'from-purple-500 to-purple-600',
      bgColor: 'bg-gradient-to-r from-purple-500/10 to-purple-600/10',
      hoverColor: 'hover:from-purple-500/20 hover:to-purple-600/20'
    }
  ];

  if (statsLoading || activitiesLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-8">
      {/* Enhanced Header with Welcome Message */}
      <div className="relative overflow-hidden bg-gradient-to-r from-gray-900 via-gray-800 to-gray-900 rounded-2xl shadow-2xl p-4 sm:p-6 lg:p-8 border border-gray-700/50">
        <div className="absolute inset-0 bg-gradient-to-r from-blue-500/5 to-purple-500/5"></div>
        <div className="relative flex flex-col sm:flex-row sm:items-center sm:justify-between space-y-4 sm:space-y-0">
          <div>
            <h1 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-white mb-2 sm:mb-3 bg-gradient-to-r from-white to-gray-300 bg-clip-text text-transparent">
              Welcome back, {user?.username || 'User'}!
            </h1>
            <p className="text-gray-300 text-base sm:text-lg font-medium">Here's what's happening at Neurons School today</p>
          </div>
          <LiveClock />
        </div>
      </div>

      {/* Festival Widget */}
      <FestivalWidget />

      {/* Enhanced Statistics Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6">
        {stats.map((stat) => (
          <Link
            key={stat.name}
            to={stat.href}
            className={`group relative overflow-hidden bg-gray-900/80 backdrop-blur-sm rounded-2xl shadow-xl p-4 sm:p-6 border ${stat.borderColor} hover:shadow-2xl transition-all duration-500 hover:scale-105 hover:bg-gray-800/90`}
          >
            <div className={`absolute inset-0 ${stat.bgColor} opacity-0 group-hover:opacity-100 transition-opacity duration-500`}></div>
            <div className="relative flex items-center justify-between">
              <div>
                <p className="text-xs sm:text-sm font-semibold text-gray-400 mb-1 sm:mb-2 uppercase tracking-wide">{stat.name}</p>
                <p className="text-2xl sm:text-3xl lg:text-4xl font-bold text-white mb-1 sm:mb-2">{stat.value}</p>
                <div className="flex items-center text-xs sm:text-sm text-gray-400 group-hover:text-white transition-colors">
                  <ArrowTrendingUpIcon className="h-3 w-3 sm:h-4 sm:w-4 mr-1 sm:mr-2" />
                  <span className="font-medium">View Details</span>
                </div>
              </div>
              <div className={`p-2 sm:p-3 lg:p-4 rounded-xl bg-gradient-to-br ${stat.color} shadow-lg group-hover:scale-110 transition-transform duration-300`}>
                <stat.icon className="h-6 w-6 sm:h-7 sm:w-7 lg:h-8 lg:w-8 text-white" />
              </div>
            </div>
          </Link>
        ))}
      </div>

      {/* Birthday Widget */}
      {birthdayStudents && birthdayStudents.length > 0 && (
        <div className="relative overflow-hidden bg-gradient-to-r from-pink-600/20 via-purple-600/20 to-blue-600/20 backdrop-blur-sm rounded-2xl shadow-xl p-4 sm:p-6 lg:p-8 border-2 border-pink-500/50 animate-pulse">
          <div className="absolute inset-0 bg-gradient-to-br from-pink-500/5 to-purple-500/5"></div>
          <div className="relative">
            <div className="flex items-center gap-3 mb-4 sm:mb-6">
              <div className="p-3 bg-gradient-to-br from-pink-500 to-purple-600 rounded-xl shadow-lg">
                <CakeIcon className="h-6 w-6 sm:h-8 sm:w-8 text-white" />
              </div>
              <div>
                <h2 className="text-xl sm:text-2xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-pink-400 via-purple-400 to-blue-400">
                  Today's Birthdays 🎉
                </h2>
                <p className="text-sm text-gray-300">
                  {birthdayStudents.length} student{birthdayStudents.length > 1 ? 's' : ''} celebrating today!
                </p>
              </div>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3 sm:gap-4">
              {birthdayStudents.map((student) => (
                <Link
                  key={student.id}
                  to={`/students/${student.id}`}
                  className="group relative overflow-hidden bg-gray-900/50 backdrop-blur-sm rounded-xl p-4 border border-pink-500/30 hover:border-pink-500/60 transition-all hover:scale-105 hover:bg-gray-800/60"
                >
                  <div className="flex items-center gap-3">
                    <div className="relative flex-shrink-0">
                      <div className="w-12 h-12 sm:w-14 sm:h-14 rounded-full bg-gradient-to-br from-pink-500 to-purple-600 p-0.5">
                        <div className="w-full h-full rounded-full bg-gray-800 flex items-center justify-center overflow-hidden">
                          {student.profile_picture_base64 ? (
                            <img
                              src={`data:image/jpeg;base64,${student.profile_picture_base64}`}
                              alt={`${student.first_name} ${student.last_name}`}
                              className="w-full h-full object-cover"
                            />
                          ) : (
                            <span className="text-lg font-bold text-white">
                              {student.first_name?.[0]}{student.last_name?.[0]}
                            </span>
                          )}
                        </div>
                      </div>
                      <div className="absolute -top-1 -right-1 text-xl">🎂</div>
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm sm:text-base font-semibold text-white truncate">
                        {student.first_name} {student.last_name}
                      </p>
                      <p className="text-xs text-gray-400 truncate">{student.student_id}</p>
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Enhanced Main Content Grid */}
      <div className="grid grid-cols-1 xl:grid-cols-3 gap-6 sm:gap-8">
        {/* Enhanced Recent Activities */}
        <div className="xl:col-span-2 relative overflow-hidden bg-gray-900/80 backdrop-blur-sm rounded-2xl shadow-xl p-4 sm:p-6 lg:p-8 border border-gray-700/50">
          <div className="absolute inset-0 bg-gradient-to-br from-blue-500/5 to-purple-500/5"></div>
          <div className="relative">
            <div className="mb-6 sm:mb-8">
              <h2 className="text-xl sm:text-2xl font-bold text-white">Recent Activities</h2>
            </div>
            <div className="space-y-3 sm:space-y-4">
              {activities && activities.length > 0 ? (
                activities.map((activity, index) => {
                  const ActivityIcon = getActivityIcon(activity.activity_type);
                  return (
                    <div key={activity.id} className="flex items-start space-x-3 sm:space-x-4 p-3 sm:p-4 rounded-xl hover:bg-gray-800/50 transition-all duration-300 border border-transparent hover:border-gray-700/50">
                      <div className="flex-shrink-0">
                        <div className="p-2 sm:p-3 bg-gray-800/80 rounded-xl border border-gray-700/50">
                          <ActivityIcon className="h-4 w-4 sm:h-5 sm:w-5 text-gray-400" />
                        </div>
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className={`text-xs sm:text-sm font-semibold ${getActivityColor(activity.activity_type)}`}>
                          {activity.description}
                        </p>
                        <p className="text-xs sm:text-sm text-gray-400 mt-1 font-medium">{activity.time_ago}</p>
                      </div>
                    </div>
                  );
                })
              ) : (
                <div className="text-center py-8 sm:py-12">
                  <div className="p-3 sm:p-4 bg-gray-800/50 rounded-full w-12 h-12 sm:w-16 sm:h-16 mx-auto mb-3 sm:mb-4 flex items-center justify-center">
                    <ClockIcon className="h-6 w-6 sm:h-8 sm:w-8 text-gray-600" />
                  </div>
                  <p className="text-gray-400 font-medium text-sm sm:text-base">No recent activities</p>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Enhanced Quick Actions */}
        <div className="relative overflow-hidden bg-gray-900/80 backdrop-blur-sm rounded-2xl shadow-xl p-4 sm:p-6 lg:p-8 border border-gray-700/50">
          <div className="absolute inset-0 bg-gradient-to-br from-green-500/5 to-blue-500/5"></div>
          <div className="relative">
            <h2 className="text-xl sm:text-2xl font-bold text-white mb-6 sm:mb-8">Quick Actions</h2>
            <div className="space-y-3 sm:space-y-4">
              {quickActions.map((action) => (
                <Link
                  key={action.name}
                  to={action.href}
                  className={`group flex items-center p-3 sm:p-4 lg:p-5 rounded-xl transition-all duration-300 ${action.bgColor} ${action.hoverColor} border border-gray-700/50 hover:border-gray-600/50 hover:shadow-lg`}
                >
                  <div className={`p-2 sm:p-3 rounded-lg bg-gradient-to-br ${action.color} mr-3 sm:mr-4 group-hover:scale-110 transition-transform duration-300`}>
                    <action.icon className="h-5 w-5 sm:h-6 sm:w-6 text-white" />
                  </div>
                  <span className="font-semibold text-white group-hover:text-gray-100 transition-colors text-sm sm:text-base">{action.name}</span>
                </Link>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Dashboard; 